import { AppState, Activity, ActivityComment } from '@/types';
import { AppAction } from '../types';
import { saveActivityToSupabase, updateActivityLikesInSupabase, saveActivityCommentToSupabase, deleteActivityFromSupabase } from '@/utils/supabaseDataSaver';

export function handleUndoLastActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'UNDO_LAST_ACTIVITY' }>
): AppState {
  const { activityId, points, activityCredits } = action.payload;

  // Remove the activity from the activities array
  const updatedActivities = state.activities.filter(a => a.id !== activityId);

  // Find the deleted activity to get its name for updating counts
  const deletedActivity = state.activities.find(a => a.id === activityId);
  
  // Update activity counts
  const updatedActivityCounts = { ...state.activityCounts };
  if (deletedActivity && updatedActivityCounts[deletedActivity.name]) {
    updatedActivityCounts[deletedActivity.name] = Math.max(0, updatedActivityCounts[deletedActivity.name] - 1);
  }

  // Delete activity from database
  deleteActivityFromSupabase(activityId).catch(error => {
    console.error('❌ Failed to delete activity from database:', error);
  });

  const newState = {
    ...state,
    activities: updatedActivities,
    activityCounts: updatedActivityCounts,
    totalPoints: Math.max(0, state.totalPoints - points),
    currentWeekPoints: Math.max(0, state.currentWeekPoints - points),
    experience: Math.max(0, state.experience - points), // Deduct experience (XP = points)
    activityCredits: Math.max(0, (state.activityCredits || 0) - activityCredits),
  };

// Save updated state to database (async)
  if (state.user?.id) {
    import('@/utils/supabaseDataSaver').then(({ saveUserDataToSupabase }) => {
      saveUserDataToSupabase(state.user!.id, newState, { strategy: 'prefer-app' }).catch(error => {
        console.error('❌ Failed to save updated user stats after undo:', error);
      });
    });
  }

  return newState;
}

export function handleLogActivity(
  state: AppState, 
  action: Extract<AppAction, { type: 'LOG_ACTIVITY' }>
): AppState {
  if (!state.user || !state.isAuthenticated) {
    console.log('❌ User not authenticated in handleLogActivity');
    return state;
  }

  const { activityType } = action.payload;
  const now = new Date();
  
  // Create new activity
  const newActivity: Activity = {
    id: crypto.randomUUID(),
    name: activityType.name,
    activityType: activityType,
    points: activityType.points,
    timestamp: now,
    description: activityType.description,
    userId: state.user.id,
    userName: state.user.name,
    likes: [],
    likedBy: [],
    comments: [],
    duration: null,
    notes: null,
    photoUrl: null,
    isOwnActivity: true
  };

  // Update activity counts
  const updatedActivityCounts = {
    ...state.activityCounts,
    [activityType.name]: (state.activityCounts[activityType.name] || 0) + 1
  };

  // Calculate streak based on calendar day, not activity timestamp
  const today = now.toDateString();
  const lastStreakDate = state.lastStreakDate;
  const isNewStreakDay = !lastStreakDate || lastStreakDate !== today;
  
  console.log('🎯 STREAK DEBUG - Direct Activity:', {
    today,
    lastStreakDate,
    isNewStreakDay,
    currentStreak: state.streak,
    activityName: activityType.name
  });
  
  const newStreak = isNewStreakDay ? state.streak + 1 : state.streak;
  const newLastStreakDate = isNewStreakDay ? today : lastStreakDate;

  console.log('🎯 Direct activity logged (POINTS + XP):', {
    activity: activityType.name,
    pointsGained: activityType.points,
    xpGained: activityType.points, // XP = Points (1:1 ratio)
    goldGained: 0
  });

  // Save activity to database
  saveActivityToSupabase(newActivity).catch(error => {
    console.error('❌ Failed to save activity to database:', error);
  });

  return {
    ...state,
    activities: [newActivity, ...state.activities],
    totalPoints: state.totalPoints + activityType.points,
    currentWeekPoints: state.currentWeekPoints + activityType.points,
    experience: state.experience + activityType.points, // Award XP equal to points
    activityCredits: state.activityCredits + activityType.points * 0.1, // Activity Credits: 10 points = 1 credit (decimals)
    streak: newStreak,
    lastStreakDate: newLastStreakDate,
    activitiesLoggedToday: state.activitiesLoggedToday + 1,
    activityCounts: updatedActivityCounts,
  };
}

export function handleLogEnhancedActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'LOG_ENHANCED_ACTIVITY' }>
): AppState {
  if (!state.user || !state.isAuthenticated) {
    return state;
  }

  const { activityType, duration, notes, photoUrl, locationData } = action.payload;
  const now = new Date();
  
  // Create enhanced activity
  const newActivity: Activity = {
    id: crypto.randomUUID(),
    name: activityType.name,
    activityType: activityType,
    points: activityType.points,
    timestamp: now,
    description: activityType.description,
    userId: state.user.id,
    userName: state.user.name,
    likes: [],
    likedBy: [],
    comments: [],
    duration,
    notes,
    photoUrl,
    latitude: locationData?.latitude,
    longitude: locationData?.longitude,
    locationAccuracy: locationData?.accuracy,
    isOwnActivity: true
  };

  // Enhanced activities get slightly better reward tokens but still no XP/Gold
  const bonusMultiplier = 1.2; // Reduced bonus since no XP/Gold

  // Calculate streak based on calendar day, not activity timestamp
  const today = now.toDateString();
  const lastStreakDate = state.lastStreakDate;
  const isNewStreakDay = !lastStreakDate || lastStreakDate !== today;
  
  const newStreak = isNewStreakDay ? state.streak + 1 : state.streak;
  const newLastStreakDate = isNewStreakDay ? today : lastStreakDate;

  // Update activity counts
  const updatedActivityCounts = {
    ...state.activityCounts,
    [activityType.name]: (state.activityCounts[activityType.name] || 0) + 1
  };

  console.log('📝 Enhanced activity logged (POINTS + XP):', {
    activity: activityType.name,
    pointsGained: activityType.points,
    xpGained: activityType.points, // XP = Points (1:1 ratio)
    goldGained: 0
  });

  // Save activity to database
  saveActivityToSupabase(newActivity).catch(error => {
    console.error('❌ Failed to save activity to database:', error);
  });

  return {
    ...state,
    activities: [newActivity, ...state.activities],
    totalPoints: state.totalPoints + activityType.points,
    currentWeekPoints: state.currentWeekPoints + activityType.points,
    experience: state.experience + activityType.points, // Award XP equal to points
    activityCredits: state.activityCredits + activityType.points * bonusMultiplier * 0.1, // Activity Credits: 10 points = 1 credit (with 1.2x bonus, decimals)
    streak: newStreak,
    lastStreakDate: newLastStreakDate,
    activitiesLoggedToday: state.activitiesLoggedToday + 1,
    activityCounts: updatedActivityCounts,
  };
}

export function handleLikeActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'LIKE_ACTIVITY' }>
): AppState {
  const { activityId, userId } = action.payload;
  
  const newState = {
    ...state,
    activities: state.activities.map(activity => {
      if (activity.id === activityId) {
        const currentLikes = activity.likes || [];
        const isLiked = currentLikes.includes(userId);
        const newLikes = isLiked 
          ? currentLikes.filter(id => id !== userId)
          : [...currentLikes, userId];
        
        // Save likes to database
        updateActivityLikesInSupabase(activityId, newLikes).catch(error => {
          console.error('❌ Failed to update likes in database:', error);
        });
        
        return {
          ...activity,
          likes: newLikes
        };
      }
      return activity;
    })
  };
  
  return newState;
}

export function handleCommentActivity(
  state: AppState,
  action: Extract<AppAction, { type: 'COMMENT_ACTIVITY' }>
): AppState {
  const { activityId, comment } = action.payload;
  
  // Save comment to database
  saveActivityCommentToSupabase(activityId, comment).catch(error => {
    console.error('❌ Failed to save comment to database:', error);
  });
  
  return {
    ...state,
    activities: state.activities.map(activity => {
      if (activity.id === activityId) {
        return {
          ...activity,
          comments: [...(activity.comments || []), comment]
        };
      }
      return activity;
    })
  };
}
